import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRoleContext } from '../hooks/useRoleContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../lib/api';
import {
  buildOwnerUpgradeRequestBody,
  OWNER_UPGRADE_REQUEST_PATH,
} from '../lib/ownerUpgradeRequestsApi';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Check,
  X,
  Loader2,
  Crown,
  Building2,
  Camera,
  BarChart3,
  Briefcase,
  ArrowRight,
  Sparkles,
  Shield,
  Users,
  Mail,
  ClipboardCheck,
  CircleDollarSign,
  Clock3,
  Gauge,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { CLIENT_PREMIUM_REDIRECT_PATH } from '../lib/pricingLaneDisplay';
import { getPricingPlanCopy, resolvePlanId } from '../lib/pricingPlanDisplay';
import { translateTemplate } from '../lib/publicListingTruth';

// NOTE: 'Verified Badge' remains excluded until owner verification renders end to end.
const PLAN_FEATURES = {
  RECON: {
    icon: Building2,
    color: 'slate',
    features: [
      { labelKey: 'plans.feature.active_listing_one', fallback: '1 active listing', included: true },
      { labelKey: 'plans.feature.photos_3', fallback: '3 photos per listing', included: true },
      { labelKey: 'plans.feature.analytics_basic', fallback: 'Basic analytics', included: true },
      { labelKey: 'plans.feature.video_links', fallback: 'Video links', included: false },
      { labelKey: 'plans.feature.weekly_kpi_reports', fallback: 'Weekly KPI reports', included: false },
      { labelKey: 'plans.feature.priority_support', fallback: 'Priority support', included: false },
    ],
  },
  OUTPOST: {
    icon: Camera,
    color: 'cyan',
    features: [
      { labelKey: 'plans.feature.active_listing_one', fallback: '1 active listing', included: true },
      { labelKey: 'plans.feature.photos_25', fallback: '25 photos per listing', included: true },
      { labelKey: 'plans.feature.analytics_standard', fallback: 'Standard analytics', included: true },
      { labelKey: 'plans.feature.video_link_one', fallback: '1 video link per listing', included: true },
      { labelKey: 'plans.feature.weekly_kpi_reports', fallback: 'Weekly KPI reports', included: false },
      { labelKey: 'plans.feature.priority_support', fallback: 'Priority support', included: false },
    ],
  },
  BASECAMP: {
    icon: BarChart3,
    color: 'blue',
    popular: true,
    features: [
      { labelKey: 'plans.feature.active_listings_5', fallback: '5 active listings', included: true },
      { labelKey: 'plans.feature.photos_25', fallback: '25 photos per listing', included: true },
      { labelKey: 'plans.feature.analytics_standard', fallback: 'Standard analytics', included: true },
      { labelKey: 'plans.feature.video_link_one', fallback: '1 video link per listing', included: true },
      { labelKey: 'plans.feature.weekly_kpi_reports', fallback: 'Weekly KPI reports', included: true },
      { labelKey: 'plans.feature.priority_support', fallback: 'Priority support', included: false },
    ],
  },
  COMMAND: {
    icon: Briefcase,
    color: 'amber',
    features: [
      { labelKey: 'plans.feature.active_listings_10', fallback: '10 active listings', included: true },
      { labelKey: 'plans.feature.photos_40', fallback: '40 photos per listing', included: true },
      { labelKey: 'plans.feature.analytics_advanced', fallback: 'Advanced analytics', included: true },
      { labelKey: 'plans.feature.video_links_2', fallback: '2 video links per listing', included: true },
      { labelKey: 'plans.feature.weekly_kpi_reports', fallback: 'Weekly KPI reports', included: true },
      { labelKey: 'plans.feature.priority_support', fallback: 'Priority support', included: true },
    ],
  },
  FORTRESS: {
    icon: Crown,
    color: 'emerald',
    features: [
      { labelKey: 'plans.feature.active_listings_25', fallback: '25 active listings', included: true },
      { labelKey: 'plans.feature.photos_50', fallback: '50 photos per listing', included: true },
      { labelKey: 'plans.feature.analytics_enterprise', fallback: 'Enterprise analytics', included: true },
      { labelKey: 'plans.feature.video_links_3', fallback: '3 video links per listing', included: true },
      { labelKey: 'plans.feature.weekly_kpi_reports', fallback: 'Weekly KPI reports', included: true },
      { labelKey: 'plans.feature.priority_support_concierge', fallback: 'Priority support plus concierge', included: true },
    ],
  },
};

const JOURNEY_STEPS = [
  {
    icon: Shield,
    labelKey: 'plans.journey.current_label',
    fallbackLabel: 'Current plan',
    textKey: 'plans.journey.current_text',
    fallbackText: 'Your active limits stay unchanged while you compare options.',
  },
  {
    icon: Gauge,
    labelKey: 'plans.journey.recommend_label',
    fallbackLabel: 'Recommended next step',
    textKey: 'plans.journey.recommend_text',
    fallbackText: 'Pick the plan that matches your listing count and media needs.',
  },
  {
    icon: ClipboardCheck,
    labelKey: 'plans.journey.request_label',
    fallbackLabel: 'Manual review',
    textKey: 'plans.journey.request_text',
    fallbackText: 'A request opens an ops review. It does not start checkout.',
  },
  {
    icon: CircleDollarSign,
    labelKey: 'plans.journey.activation_label',
    fallbackLabel: 'Activation',
    textKey: 'plans.journey.activation_text',
    fallbackText: 'Payment is confirmed manually, then the approved plan is activated.',
  },
];

function normalizePlanForCompare(value) {
  return resolvePlanId(value);
}

export default function OwnerPlansPage() {
  const navigate = useNavigate();
  const { user, isOwner, isAdmin } = useAuth();
  const { shouldUseClientPremiumPricingLane: useClientPremiumLane } = useRoleContext();
  const { t, locale, translations } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingPlan, setChangingPlan] = useState(null);
  const [confirmPlanId, setConfirmPlanId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [plansRes, myPlanRes] = await Promise.all([
          api.get('/billing/plans'),
          user ? api.get('/billing/my-plan').catch(() => null) : Promise.resolve(null),
        ]);

        if (!isMounted) return;
        setPlans(plansRes.data.plans || []);
        if (myPlanRes?.data) {
          setCurrentPlan(myPlanRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'cancelled') {
      toast.info(t('plans.toast.payment_cancelled', 'No plan change was made. Online checkout is currently off.'));
    }
    return () => {
      isMounted = false;
    };
  }, [searchParams, t, user]);

  const submitUpgradeRequest = async (planId) => {
    setChangingPlan(planId);
    try {
      const res = await api.post(
        OWNER_UPGRADE_REQUEST_PATH,
        buildOwnerUpgradeRequestBody(planId),
      );
      const duplicate = res.data?.duplicate;
      toast.success(
        duplicate
          ? t(
              'plans.upgrade_request_pending',
              'You already have a pending manual review request for this plan.',
            )
          : t(
              'plans.upgrade_request_sent',
              'Plan review request sent. Our team will confirm payment manually and activate only after approval.',
            ),
      );
      setConfirmPlanId(null);
    } catch (error) {
      toast.error(
        error.response?.data?.detail
          || t('plans.upgrade_request_failed', 'Could not submit plan review request. Please try again.'),
      );
    } finally {
      setChangingPlan(null);
    }
  };

  const currentPlanId = normalizePlanForCompare(currentPlan?.plan_id);
  const getPlanCopy = (planId) => getPricingPlanCopy(planId, locale, translations);
  const getPlanName = (planId) => {
    const normalized = normalizePlanForCompare(planId);
    const copy = getPlanCopy(normalized);
    return copy.name || normalized || t('plans.not_selected', 'Not selected');
  };

  const currentPlanIndex = plans.findIndex(
    (plan) => normalizePlanForCompare(plan.id) === currentPlanId,
  );
  const recommendedPlan = plans.find((plan, index) => (
    plan.price_monthly > 0 && (currentPlanIndex === -1 || index > currentPlanIndex)
  )) || plans.find((plan) => plan.price_monthly > 0) || null;
  const recommendedPlanId = recommendedPlan ? normalizePlanForCompare(recommendedPlan.id) : null;
  const recommendedPlanName = recommendedPlanId ? getPlanName(recommendedPlanId) : t('plans.manual_review', 'Manual review');
  const currentPlanName = currentPlanId ? getPlanName(currentPlanId) : t('plans.no_current_plan', 'No active plan loaded');
  const activeListings = currentPlan?.usage?.active_listings || 0;
  const listingLimit = currentPlan?.limits?.active_listings || 1;
  const journeyProgress = currentPlan ? 25 : 0;

  const handleChangePlan = async (planId) => {
    const normalizedPlanId = normalizePlanForCompare(planId);

    if (!user) {
      toast.error(t('plans.toast.sign_in_required', 'Please sign in to change your plan'));
      navigate('/login');
      return;
    }

    if (!isOwner && !isAdmin) {
      toast.error(t('plans.toast.owner_only', 'Only property owners can subscribe to plans'));
      return;
    }

    if (currentPlanId === normalizedPlanId) {
      toast.info(t('plans.toast.already_on_plan', 'You are already on this plan'));
      return;
    }

    const selectedPlan = plans.find((plan) => normalizePlanForCompare(plan.id) === normalizedPlanId);

    if (selectedPlan && selectedPlan.price_monthly > 0) {
      setConfirmPlanId(planId);
      return;
    }

    setChangingPlan(planId);
    try {
      const res = await api.post('/billing/set-plan', { plan_id: planId });
      toast.success(
        translateTemplate(
          t('plans.toast.plan_switched', 'Successfully switched to {plan} plan!'),
          { plan: res.data.plan_id },
        ),
      );
      setCurrentPlan({
        ...currentPlan,
        plan_id: res.data.plan_id,
        limits: res.data.limits,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('plans.toast.plan_change_failed', 'Failed to change plan'));
    } finally {
      setChangingPlan(null);
    }
  };

  const getPlanPrice = (plan) => {
    if (billingPeriod === 'yearly') {
      return plan.price_yearly;
    }
    return plan.price_monthly;
  };

  const getColorClasses = (color, isCurrentPlan) => {
    const colors = {
      slate: {
        shell: isCurrentPlan
          ? 'border-slate-500 bg-white shadow-lg shadow-slate-200/80'
          : 'border-slate-200 bg-white shadow-sm hover:border-slate-300 hover:shadow-lg',
        badge: 'bg-slate-100 text-slate-700',
        button: 'bg-slate-800 hover:bg-slate-900',
        icon: 'text-slate-700',
        accent: 'bg-slate-700',
      },
      cyan: {
        shell: isCurrentPlan
          ? 'border-cyan-500 bg-cyan-50 shadow-lg shadow-cyan-100'
          : 'border-slate-200 bg-white shadow-sm hover:border-cyan-300 hover:shadow-lg',
        badge: 'bg-cyan-50 text-cyan-700',
        button: 'bg-cyan-700 hover:bg-cyan-800',
        icon: 'text-cyan-700',
        accent: 'bg-cyan-600',
      },
      blue: {
        shell: isCurrentPlan
          ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100'
          : 'border-blue-200 bg-white shadow-md shadow-blue-100/70 hover:border-blue-400 hover:shadow-lg',
        badge: 'bg-blue-50 text-blue-700',
        button: 'bg-blue-700 hover:bg-blue-800',
        icon: 'text-blue-700',
        accent: 'bg-blue-600',
      },
      amber: {
        shell: isCurrentPlan
          ? 'border-amber-500 bg-amber-50 shadow-lg shadow-amber-100'
          : 'border-slate-200 bg-white shadow-sm hover:border-amber-300 hover:shadow-lg',
        badge: 'bg-amber-50 text-amber-700',
        button: 'bg-amber-700 hover:bg-amber-800',
        icon: 'text-amber-700',
        accent: 'bg-amber-600',
      },
      emerald: {
        shell: isCurrentPlan
          ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100'
          : 'border-slate-200 bg-white shadow-sm hover:border-emerald-300 hover:shadow-lg',
        badge: 'bg-emerald-50 text-emerald-700',
        button: 'bg-emerald-700 hover:bg-emerald-800',
        icon: 'text-emerald-700',
        accent: 'bg-emerald-600',
      },
    };
    return colors[color] || colors.slate;
  };

  const getCtaLabel = (plan, isCurrentPlan) => {
    if (isCurrentPlan) return t('plans.current_plan', 'Current plan');
    if (plan.price_monthly === 0) return t('plans.switch_free', 'Use free plan');
    return t('plans.request_plan_review', 'Request manual review');
  };

  const getRecommendedStepText = () => {
    if (!currentPlan) {
      return t(
        'plans.next_step_start',
        'Start with the free plan or request manual review for a paid owner plan.',
      );
    }
    if (!recommendedPlan) {
      return t(
        'plans.next_step_enterprise',
        'You are on the highest listed plan. Contact us for portfolio support if you need more.',
      );
    }
    return translateTemplate(
      t(
        'plans.next_step_review_plan',
        'Next best fit: request manual review for {plan} when you need more listings or media.',
      ),
      { plan: recommendedPlanName },
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  if (user && useClientPremiumLane) {
    return <Navigate to={CLIENT_PREMIUM_REDIRECT_PATH} replace />;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 pt-20 pb-16" data-testid="owner-plans-page">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-4xl text-center">
          <Badge className="mb-4 border-0 bg-signal-blue/10 text-signal-blue">
            <Crown className="w-3 h-3 mr-1" />
            {t('plans.for_owners', 'For property owners')}
          </Badge>
          <h1 className="font-heading text-4xl font-bold text-slate-900 sm:text-5xl">
            {t('plans.title', 'Owner plans')}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-slate-600">
            {t('plans.subtitle', 'Clear owner plans with transparent limits, upgrade paths, and tools built for trusted relocation housing.')}
          </p>
          <div className="mx-auto mt-6 flex max-w-3xl items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm leading-6 text-amber-950">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
            <span className="min-w-0 break-words">
              {t(
                'plans.manual_activation_banner',
                'Paid plans use manual review and payment confirmation. No checkout starts on this page.',
              )}
            </span>
          </div>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-500">
            {t('plans.tla_fee_disclosure', 'TLA bookings may include a clearly disclosed success fee when a booking or contract is confirmed.')}
          </p>
        </div>

        <section className="mb-10 grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" aria-label={t('plans.journey_aria', 'Owner plan guidance')}>
          <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="owner-plans-current-guidance">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Shield className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-500">{t('plans.current_plan', 'Current plan')}</p>
                <p className="mt-1 break-words text-2xl font-bold text-slate-900">{currentPlanName}</p>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {translateTemplate(
                    t('plans.current_usage_summary', '{used} of {limit} active listings used.'),
                    { used: activeListings, limit: listingLimit },
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="owner-plans-experience-guidance">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-500">{t('plans.recommended_step_label', 'Recommended next step')}</p>
                <p className="mt-1 break-words text-xl font-bold text-slate-900">{recommendedPlanName}</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">{getRecommendedStepText()}</p>
              </div>
              <Badge className="w-fit border border-blue-200 bg-blue-50 text-blue-700">
                <Clock3 className="mr-1 h-3 w-3" />
                {t('plans.read_only_guidance', 'Read-only guidance')}
              </Badge>
            </div>
          </div>
        </section>

        <section
          className="mb-10 rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
          data-testid="owner-plans-experience-progress"
          aria-label={t('plans.experience_progress_aria', 'Read-only plan journey progress')}
        >
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-wide text-slate-500">
                {t('plans.review_path_label', 'Plan review path')}
              </p>
              <h2 className="mt-1 font-heading text-2xl font-bold text-slate-900">
                {t('plans.review_path_title', 'What happens after a request')}
              </h2>
            </div>
            <p className="max-w-xl text-sm leading-6 text-slate-600">
              {t(
                'plans.review_path_summary',
                'This is guidance only: your current plan stays active while the team reviews fit, confirms payment manually, and activates approved changes.',
              )}
            </p>
          </div>
          <div className="mb-5 h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
            <div className="h-full rounded-full bg-signal-blue" style={{ width: `${journeyProgress}%` }} />
          </div>
          <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {JOURNEY_STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === 0 && Boolean(currentPlan);
              return (
                <li
                  key={step.labelKey}
                  className={`min-w-0 rounded-lg border p-4 ${
                    isActive ? 'border-signal-blue bg-blue-50' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                      isActive ? 'bg-signal-blue text-white' : 'bg-white text-slate-600'
                    }`}>
                      <StepIcon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 break-words text-sm font-semibold text-slate-900">
                      {t(step.labelKey, step.fallbackLabel)}
                    </span>
                  </div>
                  <p className="mt-3 break-words text-sm leading-6 text-slate-600">
                    {t(step.textKey, step.fallbackText)}
                  </p>
                </li>
              );
            })}
          </ol>
        </section>

        <div className="mb-10 flex justify-center">
          <div className="inline-flex max-w-full rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setBillingPeriod('monthly')}
              aria-pressed={billingPeriod === 'monthly'}
              className={`min-w-0 rounded-md px-4 py-2 text-sm font-semibold transition-colors sm:px-6 ${
                billingPeriod === 'monthly'
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {t('plans.monthly', 'Monthly')}
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('yearly')}
              aria-pressed={billingPeriod === 'yearly'}
              className={`min-w-0 rounded-md px-4 py-2 text-sm font-semibold transition-colors sm:px-6 ${
                billingPeriod === 'yearly'
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <span className="block sm:inline">{t('plans.yearly', 'Yearly')}</span>
              <span className={`block text-xs sm:ml-2 sm:inline ${billingPeriod === 'yearly' ? 'text-emerald-200' : 'text-emerald-700'}`}>
                {t('plans.save_17', 'Save around 17%')}
              </span>
            </button>
          </div>
        </div>

        <div
          className="grid w-full max-w-full grid-cols-1 items-stretch pt-4 gap-5 overflow-hidden sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5"
          data-testid="owner-plans-grid"
        >
          {plans.map((plan) => {
            const planId = resolvePlanId(plan.id);
            const planCopy = getPlanCopy(planId);
            const features = PLAN_FEATURES[planId];
            const isCurrentPlan = currentPlanId === planId;
            const colors = getColorClasses(features?.color || 'slate', isCurrentPlan);
            const IconComponent = features?.icon || Building2;
            const price = getPlanPrice(plan);
            const isPaid = plan.price_monthly > 0;

            return (
              <article
                key={plan.id}
                className={`relative flex h-full min-w-0 flex-col overflow-hidden rounded-lg border ${colors.shell} p-5 transition-all sm:p-6 ${
                  features?.popular ? 'z-[1] ring-2 ring-blue-400/25' : ''
                }`}
                data-testid={`owner-plan-card-${plan.id.toLowerCase()}`}
              >
                {features?.popular && (
                  <div className="pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap">
                    <Badge className="border-0 bg-blue-700 text-white shadow-sm">
                      <Sparkles className="w-3 h-3 mr-1" />
                      {t('plans.recommended', 'Recommended')}
                    </Badge>
                  </div>
                )}

                <div className={`absolute left-0 top-0 h-1 w-full ${colors.accent}`} aria-hidden="true" />

                <div className="mb-6 flex items-start justify-between gap-3 pt-2">
                  <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg ${colors.badge}`}>
                    <IconComponent className={`h-6 w-6 ${colors.icon}`} />
                  </div>
                  {isCurrentPlan && (
                    <Badge className="min-w-0 border-0 bg-emerald-600 text-white">
                      <Check className="w-3 h-3 mr-1" />
                      <span className="break-words">{t('plans.current', 'Current')}</span>
                    </Badge>
                  )}
                </div>

                <div className="mb-6 min-w-0">
                  <h3 className="break-words font-heading text-2xl font-bold text-slate-900">
                    {planCopy.name || planId}
                  </h3>
                  <p className="mt-2 min-h-[2.75rem] break-words text-sm leading-6 text-slate-600">
                    {planCopy.tagline}
                  </p>
                </div>

                <div className="mb-6 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-1 gap-y-1">
                    <span className="text-4xl font-bold tracking-normal text-slate-900">
                      €{price}
                    </span>
                    <span className="text-sm font-medium text-slate-500">
                      /{billingPeriod === 'yearly' ? t('plans.year', 'yr') : t('plans.month', 'mo')}
                    </span>
                  </div>
                  {billingPeriod === 'yearly' && plan.price_monthly > 0 && (
                    <p className="mt-2 break-words text-xs font-medium leading-5 text-emerald-700">
                      {t('plans.billed_yearly', 'Billed annually')} (€{Math.round(price / 12)}/{t('plans.month', 'mo')})
                    </p>
                  )}
                </div>

                <div className="mb-6 grid grid-cols-3 gap-2 rounded-lg bg-slate-100 p-2 text-center">
                  <div className="min-w-0 rounded-md bg-white px-2 py-3">
                    <p className="break-words text-sm font-bold text-slate-900">{plan.active_listings}</p>
                    <p className="mt-1 break-words text-xs leading-4 text-slate-500">{t('plans.listings', 'Listings')}</p>
                  </div>
                  <div className="min-w-0 rounded-md bg-white px-2 py-3">
                    <p className="break-words text-sm font-bold text-slate-900">{plan.photos_per_listing}</p>
                    <p className="mt-1 break-words text-xs leading-4 text-slate-500">{t('plans.photos', 'Photos')}</p>
                  </div>
                  <div className="min-w-0 rounded-md bg-white px-2 py-3">
                    <p className="break-words text-sm font-bold text-slate-900">{plan.video_links_per_listing}</p>
                    <p className="mt-1 break-words text-xs leading-4 text-slate-500">{t('plans.videos', 'Videos')}</p>
                  </div>
                </div>

                <ul className="mb-6 flex-1 space-y-3">
                  {features?.features.map((feature) => (
                    <li key={feature.labelKey} className="flex min-w-0 items-start gap-2">
                      {feature.included ? (
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                      ) : (
                        <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                      )}
                      <span className={`min-w-0 break-words text-sm leading-6 ${
                        feature.included ? 'text-slate-700' : 'text-slate-400'
                      }`}>
                        {t(feature.labelKey, feature.fallback)}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto min-w-0">
                  <Button
                    type="button"
                    onClick={() => handleChangePlan(plan.id)}
                    disabled={isCurrentPlan || changingPlan === plan.id}
                    className={`h-auto min-h-12 w-full whitespace-normal break-words px-3 py-3 text-center text-sm font-semibold leading-tight ${
                      isCurrentPlan
                        ? 'cursor-default border border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-100'
                        : `${colors.button} text-white`
                    }`}
                    data-testid={`select-plan-${plan.id.toLowerCase()}`}
                  >
                    {changingPlan === plan.id ? (
                      <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
                    ) : isPaid && !isCurrentPlan ? (
                      <Mail className="h-4 w-4 flex-shrink-0" />
                    ) : null}
                    <span className="min-w-0 break-words text-center">
                      {getCtaLabel(plan, isCurrentPlan)}
                    </span>
                    {!isPaid && !isCurrentPlan ? (
                      <ArrowRight className="h-4 w-4 flex-shrink-0" />
                    ) : null}
                  </Button>

                  {isPaid && !isCurrentPlan && (
                    <p className="mt-3 break-words text-center text-xs leading-5 text-slate-500">
                      {t(
                        'plans.upgrade_request_note',
                        'No checkout starts here. The team reviews the request, confirms payment manually, then activates the plan after approval.',
                      )}
                    </p>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <div className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-4 flex items-center justify-center gap-3">
              <Users className="h-6 w-6 text-amber-600" />
              <h3 className="break-words font-heading text-xl font-bold text-slate-900">
                {t('plans.need_more', 'Need more?')}
              </h3>
            </div>
            <p className="mb-6 break-words text-slate-600">
              {t('plans.enterprise_desc', 'Managing a large portfolio or need custom support? Contact us for enterprise pricing and dedicated owner review.')}
            </p>
            <Button
              type="button"
              onClick={() => navigate('/contact')}
              variant="outline"
              className="h-auto min-h-10 whitespace-normal border-slate-300 px-4 py-2 text-center"
            >
              <span className="break-words">{t('plans.contact_sales', 'Contact sales')}</span>
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={Boolean(confirmPlanId)} onOpenChange={(open) => !open && setConfirmPlanId(null)}>
        <DialogContent data-testid="owner-upgrade-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('plans.upgrade_confirm_title', 'Confirm manual plan review')}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm leading-6 text-slate-600">
                <p>
                  {t(
                    'plans.upgrade_confirm_description',
                    'You are requesting a manual review for the plan below. Your current plan stays active.',
                  )}
                </p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-left">
                  <p className="break-words">
                    <span className="font-medium text-slate-800">
                      {t('plans.upgrade_confirm_requested', 'Requested plan')}:
                    </span>{' '}
                    {confirmPlanId ? getPlanName(confirmPlanId) : ''}
                  </p>
                  {currentPlan?.plan_id && (
                    <p className="mt-1 break-words">
                      <span className="font-medium text-slate-800">
                        {t('plans.current_plan', 'Current plan')}:
                      </span>{' '}
                      {currentPlanName}
                    </p>
                  )}
                </div>
                <p>
                  {t(
                    'plans.upgrade_confirm_note',
                    'No checkout will start. Payment instructions and activation are handled manually after review.',
                  )}
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmPlanId(null)}
              disabled={changingPlan === confirmPlanId}
              className="h-auto min-h-10 whitespace-normal px-4 py-2 text-center"
            >
              <span className="break-words">{t('plans.upgrade_confirm_cancel', 'Cancel')}</span>
            </Button>
            <Button
              type="button"
              onClick={() => submitUpgradeRequest(confirmPlanId)}
              disabled={!confirmPlanId || changingPlan === confirmPlanId}
              className="h-auto min-h-10 whitespace-normal bg-signal-blue px-4 py-2 text-center text-white hover:bg-signal-blue-hover"
              data-testid="owner-upgrade-confirm-submit"
            >
              {changingPlan === confirmPlanId ? (
                <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
              ) : null}
              <span className="break-words">{t('plans.upgrade_confirm_submit', 'Confirm request')}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
